import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { generateVerpBulk } from "@/lib/verp-bulk.functions";

export const Route = createFileRoute("/_authenticated/verp-bulk")({
  component: VerpBulkPage,
  head: () => ({ meta: [{ title: "VERP Bulk Generator — InboxPulse" }] }),
});

type CampaignRow = { id: string; name: string };
type ParsedRow = { recipient_email: string; sender_email?: string; from_domain?: string };

function parseCsv(text: string): ParsedRow[] {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return [];
  // Detect header
  const firstCols = lines[0].split(/[,;\t]/).map((c) => c.trim().toLowerCase());
  const hasHeader = firstCols.some((c) =>
    ["email", "recipient", "recipient_email", "to"].includes(c),
  );
  const headers = hasHeader ? firstCols : ["recipient_email"];
  const rows: ParsedRow[] = [];
  for (let i = hasHeader ? 1 : 0; i < lines.length; i++) {
    const cols = lines[i].split(/[,;\t]/).map((c) => c.trim().replace(/^"|"$/g, ""));
    const obj: Record<string, string> = {};
    headers.forEach((h, idx) => {
      const key = h === "email" || h === "recipient" || h === "to" ? "recipient_email" : h;
      if (cols[idx]) obj[key] = cols[idx];
    });
    if (!obj.recipient_email && cols[0]) obj.recipient_email = cols[0];
    if (obj.recipient_email && /@/.test(obj.recipient_email)) {
      rows.push({
        recipient_email: obj.recipient_email,
        sender_email: obj.sender_email,
        from_domain: obj.from_domain,
      });
    }
  }
  return rows;
}

function toCsv(rows: { recipient_email: string; return_path: string }[]): string {
  const head = "recipient_email,return_path\n";
  return head + rows.map((r) => `${r.recipient_email},${r.return_path}`).join("\n") + "\n";
}

function VerpBulkPage() {
  const runBulk = useServerFn(generateVerpBulk);
  const [campaignId, setCampaignId] = useState("");
  const [text, setText] = useState("");

  const campaigns = useQuery({
    queryKey: ["campaigns-min"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("campaigns")
        .select("id, name")
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw new Error(error.message);
      return (data ?? []) as CampaignRow[];
    },
  });

  const parsed = useMemo(() => parseCsv(text), [text]);

  const mut = useMutation({
    mutationFn: async () => {
      if (!campaignId) throw new Error("Bitte eine Kampagne wählen.");
      if (parsed.length === 0) throw new Error("Keine gültigen E-Mail-Adressen erkannt.");
      return runBulk({ data: { campaign_id: campaignId, rows: parsed } });
    },
    onSuccess: (r) => {
      toast.success(
        `${r.rows.length} Return-Paths generiert (${r.created} neu, ${r.errors} Fehler)`,
      );
      // Auto-Download
      const blob = new Blob([toCsv(r.rows)], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `verp-${campaignId.slice(0, 8)}-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const onFile = async (f: File | null) => {
    if (!f) return;
    const t = await f.text();
    setText(t);
  };

  return (
    <div className="p-8 max-w-4xl space-y-6">
      <PageHeader
        title="VERP Bulk Generator"
        description="CSV mit Empfängern hochladen → eindeutige Return-Path-Adresse pro Empfänger generieren → als CSV nach Plusvibe importieren."
      />

      <HelpPanel
        title="Hilfe: Bounces zuverlässig zuordnen (VERP)"
        what={
          <>
            Tool, das dir aus deiner Empfänger-Liste eine <b>neue CSV</b> macht, in der jeder
            Empfänger eine eigene "Rückläufer-Adresse" hat. So weiß das System bei jedem Bounce,
            welcher Empfänger dahintersteckt.
          </>
        }
        why={
          <>
            Ohne VERP ist eine Bounce-Mail nur "abc@deinefirma.de hat gebounct" — du weißt nicht,
            welche Original-Mail. Mit VERP wird es eindeutig.
          </>
        }
        how={[
          <>Empfänger-CSV hochladen.</>,
          <>
            System fügt für jeden eine eigene Return-Path-Adresse hinzu (z.B.{" "}
            <code>bounce+abc123@deinefirma.de</code>).
          </>,
          <>Neue CSV herunterladen.</>,
          <>
            In Plusvibe (oder anderem Tool) als Empfänger-Liste importieren — Return-Path-Spalte als
            "Bounce-Adresse" mappen.
          </>,
        ]}
        tips={[
          <>
            Funktioniert nur, wenn die Bounce-Domain als Catch-All eingerichtet ist (siehe
            Bounce-Inbox).
          </>,
        ]}
        glossary={[
          {
            term: "VERP",
            def: "Variable Envelope Return Path. Jede Mail kriegt eine einzigartige Rückläufer-Adresse.",
          },
          {
            term: "Return-Path",
            def: "Adresse, an die Bounces zurückgeschickt werden. Anders als die sichtbare Absender-Adresse.",
          },
        ]}
      />

      <Card className="p-6 bg-card/60 border-border space-y-4">
        <div className="space-y-2">
          <Label>Kampagne</Label>
          <select
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
            className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="">— wählen —</option>
            {(campaigns.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.id.slice(0, 8)})
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground">
            Der Return-Path bindet jede Mail an diese Kampagne. Für jede Plusvibe-Kampagne eine
            eigene CSV erzeugen.
          </p>
        </div>

        <div className="space-y-2">
          <Label>CSV-Datei (oder per Textfeld unten einfügen)</Label>
          <Input
            type="file"
            accept=".csv,text/csv,text/plain"
            onChange={(e) => onFile(e.target.files?.[0] ?? null)}
          />
          <p className="text-xs text-muted-foreground">
            Spalten: <code>recipient_email</code> (Pflicht), optional <code>sender_email</code>,{" "}
            <code>from_domain</code>. Trenner: Komma / Semikolon / Tab. Header optional.
          </p>
        </div>

        <div className="space-y-2">
          <Label>Empfänger (Vorschau / manuell einfügen)</Label>
          <Textarea
            rows={10}
            placeholder={"recipient_email\nmax@example.com\nlisa@anderer.de"}
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="font-mono text-xs"
          />
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Badge variant="secondary">{parsed.length} gültige Empfänger</Badge>
            {parsed.length > 10000 && <Badge variant="destructive">Maximum 10.000 pro Lauf</Badge>}
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            onClick={() => mut.mutate()}
            disabled={mut.isPending || !campaignId || parsed.length === 0}
            className="bg-[image:var(--gradient-primary)] hover:opacity-90"
          >
            {mut.isPending ? "Generiere…" : `CSV generieren & herunterladen`}
          </Button>
        </div>
      </Card>

      <Card className="p-6 bg-card/60 border-border space-y-2 text-sm">
        <h3 className="font-semibold">So einbinden in Plusvibe</h3>
        <ol className="list-decimal pl-5 space-y-1 text-muted-foreground">
          <li>
            CSV hier exportieren — Spalten <code>recipient_email</code> und <code>return_path</code>
            .
          </li>
          <li>
            In Plusvibe als Kontaktliste importieren. <code>return_path</code> als{" "}
            <em>Custom Field</em> mappen.
          </li>
          <li>
            In der Kampagne / im SMTP-Profil als <strong>Envelope-From / Return-Path</strong> den
            Platzhalter <code>{`{{return_path}}`}</code> setzen (nicht im sichtbaren{" "}
            <code>From:</code>-Header — der bleibt deine Absender-Adresse).
          </li>
          <li>
            Wenn Plusvibe pro Kontakt keinen Envelope-From erlaubt, wird der Fallback per{" "}
            <code>Message-ID</code> verwendet (siehe Bounce-Inbox-Settings).
          </li>
        </ol>
      </Card>
    </div>
  );
}
